import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { seoRankAction, seoRankTracker } from "@/lib/seo/seo-os.functions";
import { SeoPage, Panel, Pill, Loading, Empty, DataTable, stateTone, stateLabel, num } from "@/components/admin/seo/SeoUi";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Loader2, Plus, Trash2 } from "lucide-react";

export const Route = createFileRoute("/admin/seo/rank-tracker")({
  head: () => ({ meta: [{ title: "Rank tracker · Admin" }, { name: "robots", content: "noindex, nofollow" }] }),
  component: RankTracker,
});

function RankTracker() {
  const load = useServerFn(seoRankTracker);
  const act = useServerFn(seoRankAction);
  const q = useQuery({ queryKey: ["seo-rank-tracker"], queryFn: () => load() });
  const [keyword, setKeyword] = useState("");

  const mutate = useMutation({
    mutationFn: (payload: any) => act({ data: payload }),
    onSuccess: (res: any) => {
      toast.success(res?.message ?? (res?.seeded != null ? `Added ${res.seeded} searches` : "Saved"));
      setKeyword("");
      q.refetch();
    },
    onError: (e: any) => toast.error(e?.message ?? "Action failed"),
  });

  const d = q.data as any;

  return (
    <SeoPage
      title="Rank tracker"
      description="Weekly position history for the searches you care about. Positions come from Search Console — they are the positions Google recorded for our own impressions, not a live search check."
      actions={
        <>
          <Button size="sm" variant="outline" onClick={() => mutate.mutate({ action: "seed" })} disabled={mutate.isPending}>
            Add top searches automatically
          </Button>
          <Button size="sm" onClick={() => mutate.mutate({ action: "snapshot" })} disabled={mutate.isPending}>
            {mutate.isPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
            Record this week
          </Button>
        </>
      }
    >
      {q.isLoading ? (
        <Loading />
      ) : (
        <div className="grid gap-4">
          <Panel title="Source" subtitle={d?.provider?.detail}>
            <Pill tone={stateTone(d?.provider?.state ?? "unconfigured")}>
              {stateLabel(d?.provider?.state ?? "unconfigured")}
            </Pill>
          </Panel>

          <Panel title="Tracked searches">
            <form
              className="mb-4 flex flex-wrap gap-2"
              onSubmit={(e) => {
                e.preventDefault();
                if (keyword.trim()) mutate.mutate({ action: "add", keyword: keyword.trim() });
              }}
            >
              <Input
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="e.g. dscr loan florida"
                className="max-w-xs"
              />
              <Button type="submit" size="sm" disabled={mutate.isPending}>
                <Plus className="mr-1 h-4 w-4" />
                Track
              </Button>
            </form>

            <div className="mb-4 flex flex-wrap items-center gap-2">
              <Button size="sm" variant="outline" onClick={() => mutate.mutate({ action: "refresh_volumes" })} disabled={mutate.isPending}>
                Refresh search volumes
              </Button>
              {d?.provider?.volume?.state === "ok" ? (
                <span className="text-xs text-muted-foreground">Volume data from Keywords Everywhere</span>
              ) : d?.provider?.volume?.state === "unconfigured" ? (
                <span className="text-xs text-muted-foreground">Add a Keywords Everywhere key to see search volumes</span>
              ) : null}
            </div>

            {d?.keywords?.length ? (
              <DataTable
                head={["Search", "Page", "Position", "Change", "Volume", "Impressions", "Source", ""]}
                rows={d.keywords.map((k: any) => {
                  const change =
                    k.latest?.position != null && k.previous?.position != null
                      ? Number((k.previous.position - k.latest.position).toFixed(1))
                      : null;
                  return [
                    k.keyword,
                    k.target_path ?? "—",
                    k.latest?.position != null ? k.latest.position.toFixed(1) : "No data yet",
                    change == null ? "—" : <Pill tone={change > 0 ? "good" : change < 0 ? "bad" : "neutral"}>{`${change > 0 ? "+" : ""}${change}`}</Pill>,
                    k.volume?.search_volume != null ? num(k.volume.search_volume) : "—",
                    k.latest ? num(k.latest.impressions) : "—",
                    k.latest?.source === "provider" ? "Ranking provider" : "Search Console",
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => mutate.mutate({ action: "remove", id: k.id })}
                      aria-label={`Stop tracking ${k.keyword}`}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>,
                  ];
                })}
              />
            ) : (
              <Empty>
                No searches tracked yet. Add one above, or let the system add your top performing searches automatically
                once Search Console data has been collected.
              </Empty>
            )}
          </Panel>
        </div>
      )}
    </SeoPage>
  );
}
